"use client";

/**
 * User Context and Hooks
 * Sprint 1: Data Model + Auth
 *
 * Provides:
 * - UserProvider context
 * - useUser hook for accessing current user
 * - useRequireAuth hook for protected components
 * - useRequireAdmin hook for admin-only components
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { createBrowserClient } from "@supabase/ssr";
import type { User as AuthUser } from "@supabase/supabase-js";
import type { User, Tenant, UserRole } from "@/types";
import type { TenantMembership } from "@/components/layout/tenant-switcher";

interface UserContextValue {
  // Auth state
  authUser: AuthUser | null;
  user: User | null;
  tenant: Tenant | null;
  isLoading: boolean;
  error: string | null;

  // Multi-tenant
  tenantMemberships: TenantMembership[];
  switchTenant: (tenantId: string) => Promise<void>;

  // Auth actions
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;

  // Permission checks
  isAdmin: boolean;
  isMember: boolean;
  isViewer: boolean;
  canEdit: boolean;       // admin or member (not viewer)
  canConfigure: boolean;  // admin only
  hasRole: (role: UserRole) => boolean;

  // Staff-only (internal Fluxity team, e.g. debug tooling) — never undefined
  isStaff: boolean;
}

const UserContext = createContext<UserContextValue | null>(null);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [tenantMemberships, setTenantMemberships] = useState<TenantMembership[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStaff, setIsStaff] = useState(false);

  // Memoize the supabase client to prevent infinite re-renders.
  // Fallback values prevent crashes during Next.js static page generation
  // at build time when env vars aren't available. No actual Supabase calls
  // are made during SSR (they're all in useEffect).
  const supabase = useMemo(
    () =>
      createBrowserClient(
        process.env.NEXT_PUBLIC_SUPABASE_URL || "https://placeholder.supabase.co",
        process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || "placeholder"
      ),
    []
  );

  const fetchUserData = useCallback(async (authUserId: string, authEmail?: string) => {
    try {
      const { data, error: fetchError } = await supabase
        .from("users")
        .select(
          `
          id,
          tenant_id,
          email,
          role,
          auth_user_id,
          first_name,
          last_name,
          created_at,
          updated_at,
          tenants (
            id,
            name,
            settings,
            created_at
          )
        `
        )
        .eq("auth_user_id", authUserId)
        .eq("is_active_tenant", true)
        .single();

      if (fetchError) {
        // PGRST116 = no rows found - user authenticated but no database record
        if (fetchError.code === "PGRST116") {
          // Try to create the user record via API
          if (authEmail) {
            console.log("User record not found - attempting to create...");
            try {
              const createRes = await fetch("/api/auth/setup-user", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ authUserId, email: authEmail }),
              });

              if (createRes.ok) {
                // Retry fetching user data after creation
                const { data: newData } = await supabase
                  .from("users")
                  .select(`
                    id, tenant_id, email, role, auth_user_id, first_name, last_name, created_at, updated_at,
                    tenants (id, name, settings, created_at)
                  `)
                  .eq("auth_user_id", authUserId)
                  .eq("is_active_tenant", true)
                  .single();

                if (newData) {
                  setUser({
                    id: newData.id,
                    tenant_id: newData.tenant_id,
                    email: newData.email,
                    role: newData.role as UserRole,
                    auth_user_id: newData.auth_user_id,
                    first_name: newData.first_name,
                    last_name: newData.last_name,
                    created_at: newData.created_at,
                    updated_at: newData.updated_at,
                  });
                  setTenant(newData.tenants as unknown as Tenant);
                  setError(null);
                  setIsStaff(false);
                  return;
                }
              }
            } catch (setupErr) {
              console.error("Failed to setup user:", setupErr);
            }
          }
          // If we couldn't create the user, leave as null (they'll see unauthorized)
          console.log("User record not found and could not be created");
          return;
        }
        // Auth/permission errors are expected when session is invalid or missing
        // (e.g. BroadcastChannel triggers a re-check with stale session)
        if (fetchError.code === "PGRST301" || fetchError.message?.includes("JWT")) {
          return;
        }
        console.error("Error fetching user data:", fetchError);
        setError("Failed to load user data");
        return;
      }

      if (data) {
        setUser({
          id: data.id,
          tenant_id: data.tenant_id,
          email: data.email,
          role: data.role as UserRole,
          auth_user_id: data.auth_user_id,
          first_name: data.first_name,
          last_name: data.last_name,
          created_at: data.created_at,
          updated_at: data.updated_at,
        });
        setTenant(data.tenants as unknown as Tenant);
        setError(null);
        setIsStaff(false);
      }
    } catch (err) {
      // AbortError fires when component unmounts during fetch — expected cleanup
      if (err instanceof DOMException && err.name === "AbortError") return;
      console.error("Error in fetchUserData:", err);
      setError("Failed to load user data");
    }
  }, [supabase]);

  const refreshUser = useCallback(async () => {
    const {
      data: { user: currentAuthUser },
    } = await supabase.auth.getUser();

    if (currentAuthUser) {
      await fetchUserData(currentAuthUser.id, currentAuthUser.email || undefined);
    }
  }, [supabase, fetchUserData]);

  const switchTenant = useCallback(async (tenantId: string) => {
    const response = await fetch("/api/auth/switch-tenant", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(
        typeof data.error === "string" ? data.error : "Failed to switch workspaces"
      );
    }

    window.location.reload();
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setAuthUser(null);
    setUser(null);
    setTenant(null);
    setIsStaff(false);
  }, [supabase]);

  // Initialize auth state using server-first strategy.
  // The browser Supabase client's onAuthStateChange often hangs because the
  // middleware already consumed the refresh token when loading the page.
  // Instead, we fetch session from /api/auth/me immediately (fast, reliable)
  // and use onAuthStateChange only for subsequent events (sign-in/out, token refresh).
  useEffect(() => {
    let cancelled = false;

    // Primary: fetch session from server (reads cookies directly, no client-side lock)
    (async () => {
      try {
        const res = await fetch("/api/auth/me");
        if (cancelled) return;
        if (!res.ok) {
          setIsLoading(false);
          return;
        }
        const { authUser: serverAuthUser, user: serverUser, tenant: serverTenant, memberships, isStaff: serverIsStaff } =
          await res.json();
        if (cancelled) return;
        if (serverAuthUser) setAuthUser(serverAuthUser);
        if (serverUser) {
          setUser(serverUser);
          setTenant(serverTenant);
          setError(null);
        }
        if (memberships) setTenantMemberships(memberships);
        setIsStaff(serverIsStaff === true);
      } catch (err) {
        console.error("Auth: server fetch failed:", err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    // Subscribe to subsequent auth events (sign-in, sign-out, token refresh).
    // INITIAL_SESSION may or may not fire — we don't depend on it.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (cancelled) return;

      if (event === "SIGNED_IN" && session?.user) {
        setAuthUser(session.user);
        await fetchUserData(session.user.id, session.user.email || undefined);
        setIsLoading(false);
      } else if (event === "SIGNED_OUT") {
        setAuthUser(null);
        setUser(null);
        setTenant(null);
        setIsStaff(false);
      } else if (event === "TOKEN_REFRESHED" && session?.user) {
        setAuthUser(session.user);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [supabase, fetchUserData]);

  const hasRole = useCallback((role: UserRole) => user?.role === role, [user?.role]);

  const value: UserContextValue = useMemo(() => ({
    authUser,
    user,
    tenant,
    tenantMemberships,
    switchTenant,
    isLoading,
    error,
    signOut,
    refreshUser,
    isAdmin: user?.role === "admin",
    isMember: user?.role === "member",
    isViewer: user?.role === "viewer",
    canEdit: user?.role === "admin" || user?.role === "member",
    canConfigure: user?.role === "admin",
    hasRole,
    isStaff,
  }), [authUser, user, tenant, tenantMemberships, switchTenant, isLoading, error, signOut, refreshUser, hasRole, isStaff]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

/**
 * Hook to access user context
 */
export function useUser(): UserContextValue {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }

  return context;
}

/**
 * Hook that requires authentication
 * Returns user data or redirects to login
 */
export function useRequireAuth() {
  const { user, isLoading, error } = useUser();

  return {
    user,
    isLoading,
    error,
    isAuthenticated: !!user,
  };
}

/**
 * Hook that requires admin role
 * Returns user data and admin check
 */
export function useRequireAdmin() {
  const { user, isLoading, error, isAdmin } = useUser();

  return {
    user,
    isLoading,
    error,
    isAdmin,
    isAuthorized: isAdmin,
  };
}
